import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator } from '@angular/forms';
import { safeDiv, TIME_UNIT } from 'emma-common-ts';
import { noop } from 'lodash';
import { ChangeContext } from '@angular-slider/ngx-slider';
import { EMMAFormElementComponent } from './emma-form-element.component';
import { getBestUnit, TimeUnit } from './emma-timeago.helper';
import { units, unitsMapping } from './emma-timeago.i18n';

interface Option {
  value: TIME_UNIT;
  label: string;
  min: number;
  max: number;
  factor: number;
}

const CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => EMMATimeAgoComponent),
  multi: true,
};

const VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => EMMATimeAgoComponent),
  multi: true,
};

@Component({
    selector: 'emma-timeago',
    templateUrl: './emma-timeago.component.html',
    providers: [
        CONTROL_VALUE_ACCESSOR,
        VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMATimeAgoComponent,
        },
    ],
    standalone: false
})
export class EMMATimeAgoComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  @Input() usePrefix = true;
  @Input() prefixJoin = '_';
  @Input() showOptions = [TIME_UNIT.DAYS, TIME_UNIT.WEEKS, TIME_UNIT.MONTHS, TIME_UNIT.YEARS];
  @Input() baseUnit = TIME_UNIT.DAYS;
  @Input() color?: string;
  selectedUnit = this.baseUnit;

  units: Option[] = units;
  unitsMapping = unitsMapping;
  availableUnits: Option[] = [];

  private innerValueInSeconds = 0;
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: unknown) => void = noop;

  sliderValue = this.innerValueInSeconds;
  sliderOptions = {
    hidePointerLabels: 'true',
    showSelectionBar: 'true',
    disabled: this.disabled || this.readonly,
    floor: 0,
    ceil: 0,
  };

  @Output() override $change = new EventEmitter<string | number>();
  @Output() valueChange = new EventEmitter<string | number>();

  override ngOnInit(): void {
    this.updateAvailableUnits();
  }
  override ngOnChanges(changes: SimpleChanges): void {
    if ('showOptions' in changes) {
      this.updateAvailableUnits();
    }
    if ('disabled' in changes || 'readonly' in changes) {
      this.sliderOptions = { ...this.sliderOptions, disabled: this.disabled || this.readonly };
    }
  }

  private updateAvailableUnits(): void {
    this.availableUnits = this.units.filter((u) => this.showOptions.includes(u.value));
  }

  private getUnitOption(unit: TIME_UNIT): Option {
    return this.units.find((u) => u.value === unit) || this.availableUnits[0] || this.units[0];
  }
  private getLimitedValue(unit: TIME_UNIT): number {
    const unitOption = this.getUnitOption(unit);
    return Math.min(
      Math.max(Math.round(safeDiv(this.innerValueInSeconds, unitOption.factor)), unitOption.min),
      unitOption.max
    );
  }
  private getInnerValue(unit: TIME_UNIT): number {
    if (unit === TIME_UNIT.SECONDS) {
      return this.innerValueInSeconds;
    } else {
      const unitOption = this.getUnitOption(unit);
      return safeDiv(this.innerValueInSeconds, unitOption.factor);
    }
  }
  private setInnerValue(value: number, unit: TIME_UNIT): number {
    if (unit === TIME_UNIT.SECONDS) {
      this.innerValueInSeconds = value;
    } else {
      const unitOption = this.getUnitOption(unit);
      this.innerValueInSeconds = value * unitOption.factor;
    }
    return this.innerValueInSeconds;
  }
  private setInnerValueWithPrefix(str: string): number {
    const parts = str.split(this.prefixJoin);
    if (parts.length !== 2) {
      throw Error('Invalid string with prefix value');
    }
    const value = Number(parts[1]);
    const unit = parts[0] as TIME_UNIT;
    const unitOption = this.getUnitOption(unit);
    this.innerValueInSeconds = value * unitOption.factor;
    return this.innerValueInSeconds;
  }
  private setUnit(newUnit: TIME_UNIT): void {
    if (!this.availableUnits.find((u) => u.value === newUnit)) {
      newUnit = this.availableUnits[0]?.value || this.baseUnit;
    }
    this.selectedUnit = newUnit;
    const unit = this.getUnitOption(newUnit);
    this.sliderOptions = {
      ...this.sliderOptions,
      floor: unit.min,
      ceil: unit.max,
    };
  }
  // SLIDER
  getBestUnit(): TimeUnit | undefined {
    return getBestUnit(this.innerValueInSeconds, this.availableUnits);
  }

  // OUTPUT
  private getPrefixedValue(): string {
    const value = this.getInnerValue(this.baseUnit);
    return `${this.baseUnit}${this.prefixJoin}${value || 0}`;
  }

  // SELECT CHANGES
  onUnitChange(selectedUnit: TIME_UNIT): void {
    this.setUnit(selectedUnit);
    const sliderValueRescaled = this.getLimitedValue(selectedUnit);
    this.onSliderValueChange(sliderValueRescaled);
  }
  // SLIDER CHANGES
  onSliderContextChange(sliderChangeContext: ChangeContext): void {
    this.onSliderValueChange(sliderChangeContext.value);
  }

  onSliderValueChange(sliderValue: number): void {
    this.sliderValue = sliderValue;
    const currentValue = this.innerValueInSeconds;
    this.setInnerValue(sliderValue, this.selectedUnit);
    const incomingValue = this.innerValueInSeconds;
    if (currentValue !== incomingValue) {
      const value = this.value;
      this.onChangeCallback(value);
      this.valueChange.emit(value);
      this.$change.emit(value);
    }
  }

  setBestUnit(): void {
    const unit = this.getBestUnit();
    if (unit) {
      this.setUnit(unit.value);
      const sliderValue = this.getLimitedValue(unit.value);
      if (this.sliderValue !== sliderValue) {
        this.sliderValue = sliderValue;
      }
    }
  }

  // get accessor
  get value(): string | number {
    if (this.usePrefix) {
      return this.getPrefixedValue();
    } else {
      return this.getInnerValue(this.baseUnit);
    }
  }

  // set accessor including call the onchange callback
  set value(value: string | number) {
    const currentValue = this.value;
    if ('string' === typeof value) {
      this.setInnerValueWithPrefix(value);
    } else {
      this.setInnerValue(value, this.baseUnit);
    }
    if (currentValue !== this.value) {
      this.onChangeCallback(this.value);
    }
  }

  // Set touched on blur
  onBlur(): void {
    this.onTouchedCallback();
    this.$blur.emit(this.value);
  }

  // From ControlValueAccessor interface
  writeValue(value: string | number | null): void {
    if (value !== null) {
      if ('string' === typeof value) {
        this.setInnerValueWithPrefix(value);
      } else {
        this.setInnerValue(value || 0, this.baseUnit);
      }
      this.setBestUnit();
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: (_: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }
}
